import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { Router } from '@angular/router';

import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from '@angular/fire/compat/firestore';
import { AngularFireStorage, AngularFireUploadTask } from '@angular/fire/compat/storage';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { BoardThread } from '../firestore-models/board-thread';
import { BoardComment } from '../firestore-models/board-comment';
import { ApiBoardThread, ApiBoardComment, ApiBoardFile, ApiSetBoardCommentRequest } from '../api-client-module';

import { SessionService } from '../session.service';
import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { BoardCommentDialogComponent } from './board-comment-dialog/board-comment-dialog.component';
import { BoardThreadDialogComponent } from '../board-thread/board-thread-dialog/board-thread-dialog.component';
import { TranslateService } from '@ngx-translate/core';



@Component({
  selector: 'app-board-comment',
  templateUrl: './board-comment.component.html',
  styleUrls: ['./board-comment.component.css'],
  standalone: false,
})
export class BoardCommentComponent implements OnInit, OnDestroy {

  private boardThreadDocument!: AngularFirestoreDocument<BoardThread>;
  boardThreadObservable!: Observable<BoardThread>;
  boardThreadSubscribe!: Subscription;
  private boardCommentCollection!: AngularFirestoreCollection<BoardComment>;
  boardCommentObservable!: Observable<BoardComment[]>;
  boardCommentSubscribe!: Subscription;

  boardThread!: BoardThread;

  //フロア情報テーブル
  displayedColumns: string[] = ['comment_owner', 'created', 'comment_desc', 'comment_image'];
  dataSource = new MatTableDataSource<BoardComment>();
  selection = new SelectionModel<BoardComment>(true, []);

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  task!: AngularFireUploadTask;
  owner_email: string = '';
  threadId: string = '';

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private configurationService: ConfigurationService,
    private storage: AngularFireStorage,
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.threadId = this.configurationService.getThreadId();
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.boardThreadDocument = this.angularFirestore.collection<BoardThread>(AppConst.FIRESTORE_BOARD_THREAD).doc(this.threadId);
    this.boardThreadObservable = this.boardThreadDocument.valueChanges({ idField: 'id' }) as any;
    this.boardThreadSubscribe = this.boardThreadObservable.subscribe(boardThread => {
      this.boardThread = boardThread;
    });

    this.boardCommentCollection = this.angularFirestore.collection<BoardComment>(AppConst.FIRESTORE_BOARD_COMMENT, ref => ref.where('thread_id', '==', this.threadId).orderBy('created', 'desc'));
    this.boardCommentObservable = this.boardCommentCollection.valueChanges({ idField: 'id' });
    this.boardCommentSubscribe = this.boardCommentObservable.subscribe(boardComments => {
      this.dataSource.data = boardComments;
    });
    this.owner_email = this.sessionService.auth.currentUser?.email!;
  }

  ngOnDestroy(): void {
    this.boardCommentSubscribe.unsubscribe();
  }

  openThreadBrowseDialog(): void {
    let apiBoardThread: ApiBoardThread = {
      id: this.boardThread.id,
      thread_name: this.boardThread.thread_name,
      thread_desc: this.boardThread.thread_desc,
      thread_image: this.boardThread.thread_image,
      thread_owner: this.boardThread.thread_owner
    };
    const dialogRef = this.dialog.open(BoardThreadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardThread: apiBoardThread, isBrowse: true }
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }


  openBrowseDialog(boardComment: BoardComment): void {
    let apiBoardComment: ApiBoardComment = {
      id: boardComment.id,
      thread_id: this.threadId,
      comment_desc: boardComment.comment_desc,
      comment_image: boardComment.comment_image,
      comment_owner: boardComment.comment_owner
    };
    const dialogRef = this.dialog.open(BoardCommentDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardComment: apiBoardComment, isBrowse: true }
    });
    dialogRef.afterClosed().subscribe(result => {
    });
  }

  openEditDialog(boardComment: BoardComment): void {
    let apiBoardComment: ApiBoardComment = {
      id: boardComment.id,
      thread_id: this.threadId,
      comment_desc: boardComment.comment_desc,
      comment_image: boardComment.comment_image,
      comment_owner: boardComment.comment_owner
    };
    const dialogRef = this.dialog.open(BoardCommentDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardComment: apiBoardComment, isBrowse: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.spinnerService.openSpinner();
        if (result.isDelete === false) {
          //  更新処理
          if (!!result.file) {
            //  添付ファイルあり（イメージ再設定の場合）
            const fileName = `${Date.now()}_${result.file.name}`;
            const path = `${AppConst.FILE_UPLOAD_BOARD_DIR_NAME}/${fileName}`;
            const ref = this.storage.ref(path);

            this.task = this.storage.upload(path, result.file);
            this.task.snapshotChanges().pipe(
              finalize(() => {
                ref.getDownloadURL().subscribe(downloadURL => {
                  let boardFile: ApiBoardFile = {
                    file_name: fileName,
                    file_path: path,
                    download_url: downloadURL
                  };
                  let boardComment: ApiBoardComment = {
                    id: result.id,
                    thread_id: this.threadId,
                    comment_desc: result.comment_desc,
                    comment_image: boardFile,
                    comment_owner: this.sessionService.auth.currentUser?.email!,
                  }; 
                  let request: ApiSetBoardCommentRequest = {
                    access_token: '',
                    change_type: AppConst.DATA_CHANGE_TYPE_UPDATE,
                    board_comments: [boardComment]
                  }
                  this.apiClientService.setBoardComment(request).then(
                    response => {
                      if (response!.ret === AppConst.API_RET_SUCCESS) {
                        this.toastr.success(this.translate.instant('更新しました。'));
                      } else {
                        this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
                      }
                    }
                  ).catch(
                    error => {
                      console.log(error);
                      this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
                    }
                  ).finally(
                    () => {
                      this.spinnerService.closeSpinner();
                    }
                  );
                });
              })
            ).subscribe();
          } else {
            //  添付ファイルなし
            let boardComment: ApiBoardComment;
            if (!!result.comment_image) {
              let boardFile: ApiBoardFile = {
                file_name: result.comment_image.file_name,
                file_path: result.comment_image.file_path,
                download_url: result.comment_image.download_url
              };
              boardComment = {
                id: result.id,
                thread_id: this.threadId,
                comment_desc: result.comment_desc,
                comment_image: boardFile,
                comment_owner: this.sessionService.auth.currentUser?.email!,
              };
            } else {
              //  既存イメージなし
              boardComment = {
                id: result.id,
                thread_id: this.threadId,
                comment_desc: result.comment_desc,
                comment_owner: this.sessionService.auth.currentUser?.email!,
              };
            }
            let request: ApiSetBoardCommentRequest = {
              access_token: '',
              change_type: AppConst.DATA_CHANGE_TYPE_UPDATE,
              board_comments: [boardComment]
            }
            this.apiClientService.setBoardComment(request).then(
              response => {
                if (response!.ret === AppConst.API_RET_SUCCESS) {
                  this.toastr.success(this.translate.instant('更新しました。'));
                } else {
                  this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
                }
              }
            ).catch(
              error => {
                console.log(error);
                this.toastr.error(this.translate.instant('更新できませんでした。'), '', { timeOut : 5000 });
              }
            ).finally(
              () => {
                this.spinnerService.closeSpinner();
              }
            );
          }
        } else {
          //  削除処理
          let request: ApiSetBoardCommentRequest = {
            access_token: '',
            change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
            board_comments: [boardComment]
          }
          this.apiClientService.setBoardComment(request).then(
            response => {
              if (response!.ret === AppConst.API_RET_SUCCESS) {
                this.toastr.success(this.translate.instant('削除しました。'));
              } else {
                this.toastr.error(this.translate.instant('削除できませんでした。'));
              }
            }
          ).catch(
            error => {
              console.log(error);
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          ).finally(
            () => {
              this.spinnerService.closeSpinner();
            }
          );
        }
      }
    });
  }

  openAddDialog(): void {
    const dialogRef = this.dialog.open(BoardCommentDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { boardComment: null, isBrowse: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.spinnerService.openSpinner();
        if (!!result.file) {
          //  添付ファイルあり
          const fileName = `${Date.now()}_${result.file.name}`;
          const path = `${AppConst.FILE_UPLOAD_BOARD_DIR_NAME}/${fileName}`;
          const ref = this.storage.ref(path);

          this.task = this.storage.upload(path, result.file);
          this.task.snapshotChanges().pipe(
            finalize(() => {
              ref.getDownloadURL().subscribe(downloadURL => {
                let boardFile: ApiBoardFile = {
                  file_name: fileName,
                  file_path: path,
                  download_url: downloadURL
                };
                let boardComment: ApiBoardComment = {
                  thread_id: this.threadId,
                  comment_desc: result.comment_desc,
                  comment_image: boardFile,
                  comment_owner: this.sessionService.auth.currentUser?.email!,
                };
                let request: ApiSetBoardCommentRequest = {
                  access_token: '',
                  change_type: AppConst.DATA_CHANGE_TYPE_ADD,
                  board_comments: [boardComment]
                }
                this.apiClientService.setBoardComment(request).then(
                  response => {
                    if (response!.ret === AppConst.API_RET_SUCCESS) {
                      this.toastr.success(this.translate.instant('登録しました。'));
                    } else {
                      this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
                    }
                  }
                ).catch(
                  error => {
                    console.log(error);
                    this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
                  }
                ).finally(
                  () => {
                    this.spinnerService.closeSpinner();
                  }
                );
              });
            })
          ).subscribe();
        } else {
          //  添付ファイルなし
          let boardComment: ApiBoardComment = {
            thread_id: this.threadId,
            comment_desc: result.comment_desc,
            comment_owner: this.sessionService.auth.currentUser?.email!,
          };
          let request: ApiSetBoardCommentRequest = {
            access_token: '',
            change_type: AppConst.DATA_CHANGE_TYPE_ADD,
            board_comments: [boardComment]
          }
          this.apiClientService.setBoardComment(request).then(
            response => {
              if (response!.ret === AppConst.API_RET_SUCCESS) {
                this.toastr.success(this.translate.instant('登録しました。'));
              } else {
                this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
              }
            }
          ).catch(
            error => {
              console.log(error);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          ).finally(
            () => {
              this.spinnerService.closeSpinner();
            }
          );
        }
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'created':
          return compare(a.created.toString(), b.created.toString(), isAsc);
        case 'comment_desc':
          return compare(a.comment_desc, b.comment_desc, isAsc);
        case 'comment_owner':
          return compare(a.comment_owner, b.comment_owner, isAsc);
        default:
          return 0;
      }
    });
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
